import { useMemo } from "react"

import { AxiosError } from "axios"
import { Formik, Form } from "formik"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { STATUS_CODE_UNAUTHORIZED } from "@l2r-front/l2r-query"

import { I18N_NAMESPACE } from "../../constants/i18n"

import { validationSchema } from "./LoginForm.schema"
import * as Styled from "./LoginForm.styled"

export const LoginForm = (props: LoginFormProps) => {

    const {
        initialValues = {
            email: "",
            password: "",
        },
        linkToRecoverPassword,
        onSubmit,
        className,
    } = props

    const { t } = useTranslation([I18N_NAMESPACE])

    const formikProps = useMemo(() => {
        interface FormValues {
            email: string
            password: string
        }

        interface FormikStatusHelpers {
            setStatus: (status: string | null) => void
        }

        interface FormikProps {
            validationSchema: ReturnType<typeof validationSchema>
            initialValues: FormValues
            onSubmit: (values: FormValues, formikHelpers: FormikStatusHelpers) => Promise<void>
        }

        return {
            validationSchema: validationSchema(t),
            initialValues: initialValues,
            onSubmit: async (values: FormValues, { setStatus }: FormikStatusHelpers) => {
                try {
                    setStatus(null)
                    await onSubmit(values)
                } catch (error: unknown) {
                    const err = error as AxiosError
                    if (err.response?.status === STATUS_CODE_UNAUTHORIZED) {
                        setStatus(t(I18N_NAMESPACE, "forms.loginForm.errors.incorrectCredentials"))
                        return
                    }
                    setStatus(t(I18N_NAMESPACE, "forms.loginForm.errors.unknown"))
                }
            },
        } as FormikProps
    }, [initialValues, onSubmit, t])

    return (
        <Formik<{ email: string; password: string }, { status?: string | null }>
            {...formikProps}
        >
            {(formikState: {
                status?: string | null
            }) => (
                <Form className={className}>
                    {formikState.status && (
                        <Styled.FormHelperText error id="submit-error">{formikState.status}</Styled.FormHelperText>
                    )}
                    <Styled.FormikTextField
                        autoFocus
                        name="email"
                        id="email"
                        type="email"
                        label={t(I18N_NAMESPACE, "forms.loginForm.fields.email.label")}
                    />
                    <Styled.FormikTextField
                        type="password"
                        id="password"
                        name="password"
                        label={t(I18N_NAMESPACE, "forms.loginForm.fields.password.label")}
                    />
                    <Styled.SubmitWrapper>
                        {linkToRecoverPassword}
                        <Styled.FormikButtonSubmit>
                            {t(I18N_NAMESPACE, "forms.loginForm.actions.login")}
                        </Styled.FormikButtonSubmit>
                    </Styled.SubmitWrapper>
                </Form>
            )}
        </Formik>
    )
}

type LoginFormProps = {
    className?: string
    initialValues?: {
        email: string
        password: string
    }
    onSubmit: (values: { email: string; password: string }) => Promise<void>
    linkToRecoverPassword?: React.ReactNode
}
